import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { Material } from '@babylonjs/core/Materials/material';
import { type Node } from '@babylonjs/core/node';
import { type Scene } from '@babylonjs/core/scene';
import { vertexDataCreate } from './vertexData';
import { type VertexDataDesc } from './meshTypes';

export class MeshExt {
  /**
   * Creates a new mesh.
   * @param id - The value used by scene.getMeshByName() to do a lookup.
   * @param scene - The scene to add this mesh to.
   * @param parent - The parent of this mesh, if it has one.
   */
  static create(id: string, scene: Scene, parent?: Node): Mesh {
    const mesh = new Mesh(id, scene, parent);
    MeshExt.configure(mesh);
    return mesh;
  }

  /**
   * Applies new geometry from data.
   * @param mesh - The mesh the geometry data is applied to.
   * @param geometry - The geometry data.
   * @param updatable - When used and having the value true allows new data to update the vertexData.
   */
  static applyGeometry(mesh: Mesh, geometry: VertexDataDesc, updatable?: boolean) {
    const vertexData = vertexDataCreate(geometry);
    vertexData.applyToMesh(mesh, updatable);
  }

  /**
   * Configures mesh.
   * @param mesh - The mesh.
   */
  static configure(mesh: Mesh) {
    mesh.overrideMaterialSideOrientation = Material.CounterClockWiseSideOrientation;
  }

  /**
   * Updates mesh positions and normals.
   * @param mesh - The mesh.
   * @param data - The vertex data.
   */
  static updateMeshPositions(mesh: Mesh, data: Float32Array, computeNormals = true) {
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    if (!positions) {
      return;
    }
    (positions as Float32Array).set(data);
    mesh.updateVerticesData(VertexBuffer.PositionKind, positions, true, false);
    if (computeNormals) {
      const indices = mesh.getIndices();
      const normals = mesh.getVerticesData(VertexBuffer.NormalKind);
      if (!normals) {
        return;
      }
      VertexData.ComputeNormals(positions, indices, normals);
      mesh.updateVerticesData(VertexBuffer.NormalKind, normals, false, false);
    }
  }
}
